import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { RecordPoint } from "../fit/types";

interface Props {
  records: RecordPoint[];
  splits: number[];
  hoverIndex: number | null;
  selected: number | null;
  highlight: { startIdx: number; endIdx: number } | null;
  focusRange: { startIdx: number; endIdx: number; key: number } | null;
  describeIndex: (index: number) => string;
  onHover: (index: number | null) => void;
  onAddSplit: (index: number) => void;
  onMoveSplitAt: (pos: number, index: number) => void;
  onCommitMove: () => void;
  onRemoveSplitAt: (pos: number) => void;
  onSelect: (pos: number | null) => void;
}

interface Positioned {
  idx: number;
  lat: number;
  lng: number;
}

export default function MapView(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const routeRef = useRef<L.Polyline | null>(null);
  const highlightRef = useRef<L.Polyline | null>(null);
  const lapLayerRef = useRef<L.LayerGroup | null>(null);
  const endsLayerRef = useRef<L.LayerGroup | null>(null);
  const hoverRef = useRef<L.CircleMarker | null>(null);
  const positionedRef = useRef<Positioned[]>([]);

  // Keep the latest props in a ref so imperative Leaflet handlers stay current.
  const propsRef = useRef(props);
  propsRef.current = props;

  const nearestPositioned = (lat: number, lng: number): number => {
    const pts = positionedRef.current;
    let best = pts.length ? pts[0].idx : 0;
    let bestD = Infinity;
    for (const p of pts) {
      const dLat = p.lat - lat;
      const dLng = p.lng - lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) {
        bestD = d;
        best = p.idx;
      }
    }
    return best;
  };

  const clampBetweenNeighbours = (pos: number, index: number): number => {
    const { splits, records } = propsRef.current;
    const lower = pos > 0 ? splits[pos - 1] + 1 : 1;
    const upper =
      pos < splits.length - 1 ? splits[pos + 1] - 1 : records.length - 2;
    return Math.min(Math.max(index, lower), upper);
  };

  // Initialise the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([0, 0], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      if (positionedRef.current.length === 0) return;
      const idx = nearestPositioned(e.latlng.lat, e.latlng.lng);
      propsRef.current.onAddSplit(idx);
    });

    lapLayerRef.current = L.layerGroup().addTo(map);
    endsLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Draw the route whenever records change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const positioned: Positioned[] = [];
    const latlngs: L.LatLngExpression[] = [];
    for (const r of props.records) {
      if (r.lat !== null && r.lng !== null) {
        positioned.push({ idx: r.index, lat: r.lat, lng: r.lng });
        latlngs.push([r.lat, r.lng]);
      }
    }
    positionedRef.current = positioned;

    routeRef.current?.remove();
    if (latlngs.length === 0) {
      routeRef.current = null;
      return;
    }
    const route = L.polyline(latlngs, {
      color: "#38bdf8",
      weight: 4,
      opacity: 0.9,
    }).addTo(map);
    route.on("mousemove", (e: L.LeafletMouseEvent) => {
      propsRef.current.onHover(nearestPositioned(e.latlng.lat, e.latlng.lng));
    });
    route.on("mouseout", () => propsRef.current.onHover(null));
    routeRef.current = route;
    map.fitBounds(route.getBounds(), { padding: [24, 24] });

    // start / finish flags
    const ends = endsLayerRef.current;
    if (ends) {
      ends.clearLayers();
      const first = positioned[0];
      const last = positioned[positioned.length - 1];
      if (first)
        L.marker([first.lat, first.lng], {
          icon: endIcon("start", "S"),
          interactive: false,
        }).addTo(ends);
      if (last)
        L.marker([last.lat, last.lng], {
          icon: endIcon("finish", "F"),
          interactive: false,
        }).addTo(ends);
    }
  }, [props.records]);

  // Highlighted lap trace overlay (sits above the route, below the markers).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    highlightRef.current?.remove();
    highlightRef.current = null;
    const hr = props.highlight;
    if (!hr) return;
    const latlngs: L.LatLngExpression[] = [];
    for (const r of props.records) {
      if (
        r.index >= hr.startIdx &&
        r.index <= hr.endIdx &&
        r.lat !== null &&
        r.lng !== null
      ) {
        latlngs.push([r.lat, r.lng]);
      }
    }
    if (latlngs.length < 2) return;
    highlightRef.current = L.polyline(latlngs, {
      color: "#f97316",
      weight: 7,
      opacity: 0.95,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(map);
  }, [props.highlight, props.records]);

  // Zoom to a lap when requested (its table row was clicked).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !props.focusRange) return;
    const { startIdx, endIdx } = props.focusRange;
    const latlngs: L.LatLngTuple[] = [];
    for (const r of props.records) {
      if (
        r.index >= startIdx &&
        r.index <= endIdx &&
        r.lat !== null &&
        r.lng !== null
      ) {
        latlngs.push([r.lat, r.lng]);
      }
    }
    if (latlngs.length === 0) return;
    map.flyToBounds(L.latLngBounds(latlngs), {
      padding: [40, 40],
      maxZoom: 16,
      duration: 0.5,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusRange]);

  // Redraw lap boundary markers when splits / selection change.
  useEffect(() => {
    const layer = lapLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const posById = new Map(positionedRef.current.map((p) => [p.idx, p]));

    props.splits.forEach((idx, pos) => {
      const p = posById.get(idx) ?? nearestPositionedPoint(idx);
      if (!p) return;
      const marker = L.marker([p.lat, p.lng], {
        icon: lapIcon(pos + 1, props.selected === pos),
        draggable: true,
        autoPan: true,
      }).addTo(layer);
      marker.bindTooltip("", {
        direction: "top",
        offset: [0, -38],
        className: "map-drag-tip",
        opacity: 1,
      });

      marker.on("drag", () => {
        const ll = marker.getLatLng();
        const nearIdx = clampBetweenNeighbours(
          pos,
          nearestPositioned(ll.lat, ll.lng),
        );
        propsRef.current.onMoveSplitAt(pos, nearIdx);
        propsRef.current.onHover(nearIdx);
        marker.setTooltipContent(propsRef.current.describeIndex(nearIdx));
        if (!marker.isTooltipOpen()) marker.openTooltip();
      });
      marker.on("dragend", () => {
        marker.closeTooltip();
        propsRef.current.onCommitMove();
        const target = posById.get(propsRef.current.splits[pos]);
        if (target) marker.setLatLng([target.lat, target.lng]);
      });
      marker.on("click", () => {
        propsRef.current.onSelect(props.selected === pos ? null : pos);
      });
      marker.bindPopup(
        `<button class="map-remove-btn" data-pos="${pos}">Remove lap boundary</button>`,
      );
      marker.on("popupopen", (e: L.PopupEvent) => {
        const btn = e.popup
          .getElement()
          ?.querySelector<HTMLButtonElement>(".map-remove-btn");
        btn?.addEventListener("click", () => {
          map_removeSplit(pos);
          marker.closePopup();
        });
      });
    });

    function map_removeSplit(pos: number) {
      propsRef.current.onRemoveSplitAt(pos);
    }
    function nearestPositionedPoint(idx: number): Positioned | undefined {
      const pts = positionedRef.current;
      if (pts.length === 0) return undefined;
      let best = pts[0];
      let bestD = Math.abs(pts[0].idx - idx);
      for (const p of pts) {
        const d = Math.abs(p.idx - idx);
        if (d < bestD) {
          bestD = d;
          best = p;
        }
      }
      return best;
    }
  }, [props.splits, props.selected, props.records]);

  // Move the hover highlight.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const { hoverIndex, records } = props;
    if (hoverIndex === null) {
      hoverRef.current?.remove();
      hoverRef.current = null;
      return;
    }
    const rec = records[hoverIndex];
    if (!rec || rec.lat === null || rec.lng === null) return;
    if (!hoverRef.current) {
      hoverRef.current = L.circleMarker([rec.lat, rec.lng], {
        radius: 6,
        color: "#ffffff",
        weight: 2,
        fillColor: "#f97316",
        fillOpacity: 1,
      }).addTo(map);
    } else {
      hoverRef.current.setLatLng([rec.lat, rec.lng]);
    }
  }, [props.hoverIndex, props.records]);

  return <div ref={containerRef} className="map-view" />;
}

function lapIcon(n: number, selected: boolean): L.DivIcon {
  const fill = selected ? "#f97316" : "#2563eb";
  const html = `
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg" class="pin-svg">
      <path d="M15 39C15 39 27 24 27 13.5A12 12 0 1 0 3 13.5C3 24 15 39 15 39Z"
            fill="${fill}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="15" cy="13.5" r="7" fill="#ffffff"/>
      <text x="15" y="13.9" text-anchor="middle" dominant-baseline="middle"
            font-family="system-ui, sans-serif" font-size="11" font-weight="700"
            fill="${fill}">${n}</text>
    </svg>`;
  return L.divIcon({
    className: "pin-icon",
    html,
    iconSize: [30, 40],
    iconAnchor: [15, 39],
    popupAnchor: [0, -36],
  });
}

function endIcon(kind: "start" | "finish", label: string): L.DivIcon {
  const fill = kind === "start" ? "#16a34a" : "#dc2626";
  const html = `
    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" class="pin-svg">
      <circle cx="10" cy="10" r="8" fill="${fill}" stroke="#ffffff" stroke-width="2.5"/>
      <text x="10" y="10.4" text-anchor="middle" dominant-baseline="middle"
            font-family="system-ui, sans-serif" font-size="9" font-weight="700"
            fill="#ffffff">${label}</text>
    </svg>`;
  return L.divIcon({
    className: "pin-icon",
    html,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}
